/**
 * The API client. Cookie-based, so there is no token to store and nothing to leak to
 * localStorage. A 401 triggers one refresh attempt and one retry; only the hub refusing the refresh
 * sends the person to sign in. If the hub cannot be reached (network, 5xx), the error is not a 401.
 */
/**
 * Empty by default, so requests go to the same origin and the Vite dev proxy handles `/api`.
 * That keeps `SameSite=Strict` cookies working in dev exactly as they do in production.
 *
 * Only set VITE_API_URL when the API really is on another origin — and if you do, read
 * docs/02-auth.md first: the cookies are SameSite=Strict and the hub expects to be same-origin.
 */
const BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
    readonly fields?: { path: string; message: string }[],
  ) {
    super(message);
  }
}

/**
 * What a refresh attempt says. Only the hub answering no is a sign-out; a network error, a 5xx or
 * a 429 (a deploy's dyno swap, Heroku's router answering 503) says nothing about the session.
 */
type RefreshResult = 'ok' | 'denied' | 'unavailable';

let refreshing: Promise<RefreshResult> | null = null;

async function refreshOnce(): Promise<RefreshResult> {
  // Collapse concurrent 401s into one refresh — otherwise six parallel queries each rotate the
  // refresh token and five of them get a reuse error, which revokes the family.
  refreshing ??= fetch(`${BASE}/api/id/auth/refresh`, { method: 'POST', credentials: 'include' })
    .then((r): RefreshResult => (r.ok ? 'ok' : r.status >= 500 || r.status === 429 ? 'unavailable' : 'denied'))
    .catch((): RefreshResult => 'unavailable')
    .finally(() => {
      setTimeout(() => (refreshing = null), 0);
    });
  return refreshing;
}

/** Status 0: the request never reached the hub. Treated like a 503, never like a sign-out. */
const UNAVAILABLE = 'Hub is not answering right now. Try again in a moment.';

/**
 * The auth calls that must not trigger a refresh: they either are the refresh, end the session, or
 * run without one (a 401 from login is a wrong password, not an expired session). Everything else
 * under /api/id/auth, /auth/me above all, refreshes like any call: the access cookie lasts 15
 * minutes, so without this a reload after a quiet spell showed Sign in despite a valid session.
 */
const NO_REFRESH = ['/api/id/auth/login', '/api/id/auth/refresh', '/api/id/auth/logout', '/api/id/auth/set-password'];

async function request<T>(path: string, init: RequestInit = {}, isRetry = false): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...init.headers },
    });
  } catch {
    throw new ApiError(0, UNAVAILABLE);
  }

  if (res.status === 401 && !isRetry && !NO_REFRESH.some((p) => path.startsWith(p))) {
    const refreshed = await refreshOnce();
    if (refreshed === 'ok') return request<T>(path, init, true);
    // The session may be fine; the hub just could not be asked. Not a 401, so nobody is signed out.
    if (refreshed === 'unavailable') throw new ApiError(503, UNAVAILABLE);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let body: Record<string, unknown> = {};
  try {
    body = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    // Not JSON: an HTML error page from the router or proxy during a deploy.
    if (!res.ok) throw new ApiError(res.status, res.status >= 500 ? UNAVAILABLE : 'Something went wrong.');
  }

  if (!res.ok) {
    throw new ApiError(
      res.status,
      (body.error as string) ?? 'Something went wrong.',
      body.detail,
      body.fields as { path: string; message: string }[] | undefined,
    );
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export const qs = (params: Record<string, string | number | undefined | null>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

